// The wall: the console refusing to show a page. A composed page, not a
// form column: a letterhead at the top so the person knows where they are,
// then the statement at display size on the left, and on the right the
// report slip, a Page White card an administrator copies whole and sends
// to MotionSafe. On a phone the slip follows the statement.
//
// The state is named in the heading, the way every other state in the
// console is a word. One rule in the state color crosses the top of the
// slip; nothing else on the page is colored. Three kinds: a fault
// is Vermillion (the Calm Red Rule, a state that already happened), a service
// not yet set up is Workshop Amber, a missing page is neutral.

import { Box, Button, CopyButton, Group, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import type { ReactNode } from 'react';

import { EYEBROW, ICON_INLINE } from '../theme';
import { Wordmark } from './logo';

export type WallKind = 'fault' | 'setup' | 'missing';

// The state word goes into the copied report; on the page the heading says
// it and the rule across the top carries the color.
const KIND: Record<WallKind, { word: string; rule: string }> = {
  fault: { word: 'failed', rule: 'var(--mantine-color-danger-filled)' },
  setup: { word: 'not set up', rule: 'var(--mantine-color-warning-filled)' },
  missing: { word: 'not found', rule: 'var(--mantine-color-default-border)' },
};

export interface WallDetail {
  label: string;
  value: string;
}

export interface WallProps {
  kind: WallKind;
  title: string;
  /** What happened, one sentence. */
  what: ReactNode;
  /** Who fixes it, one sentence. Omitted for a missing page. */
  who?: ReactNode | undefined;
  /** Facts worth relaying: the service's reason, the page, the moment. */
  details?: WallDetail[] | undefined;
  primary?: { label: string; onClick?: (() => void) | undefined; href?: string | undefined } | undefined;
  secondary?: { label: string; href: string } | undefined;
}

/** The slip as plain text, for the clipboard: one line per fact. */
function slipText(kind: WallKind, title: string, details: WallDetail[]): string {
  return [`MotionSafe Console: ${title}`, `State: ${KIND[kind].word}`, ...details.map((d) => `${d.label}: ${d.value}`)].join('\n');
}

export function Wall({ kind, title, what, who, details = [], primary, secondary }: WallProps) {
  const { rule } = KIND[kind];
  const hasSlip = details.length > 0;
  return (
    <Box component="main" mih="100dvh" style={{ display: 'grid', gridTemplateRows: '1fr auto' }}>
      <Box px={{ base: 'md', sm: 'xl' }} py="xl" style={{ display: 'grid', placeItems: 'center' }}>
        <Stack gap="lg" w="100%" maw={hasSlip ? 960 : 600}>
          <Wordmark size={18} />

          <Paper p={0} style={{ overflow: 'hidden', borderTop: `2px solid ${rule}` }} role={kind === 'fault' ? 'alert' : 'status'}>
            <SimpleGrid cols={{ base: 1, md: hasSlip ? 2 : 1 }} spacing={0} style={{ gridTemplateColumns: hasSlip ? undefined : '1fr' }}>
              <Stack gap="xl" p={{ base: 'lg', md: 'xl' }} justify="space-between">
                <Stack gap="md">
                  <Title order={1}>{title}</Title>
                  <Text>{what}</Text>
                  {who && <Text c="dimmed">{who}</Text>}
                </Stack>
                {(primary || secondary) && (
                  <Group gap="sm">
                    {/* Instrument Blue marks where doing will happen, and on a
                        fault or a service not yet set up there is nothing the
                        person can do: every action is quiet. A missing page
                        keeps its one real move. */}
                    {primary && (primary.href
                      ? <Button variant={kind === 'missing' ? 'filled' : 'default'} component="a" href={primary.href}>{primary.label}</Button>
                      : <Button variant={kind === 'missing' ? 'filled' : 'default'} onClick={primary.onClick}>{primary.label}</Button>)}
                    {secondary && <Button variant="default" component="a" href={secondary.href}>{secondary.label}</Button>}
                  </Group>
                )}
              </Stack>

              {hasSlip && (
                <Stack gap={0} style={{ background: 'var(--mantine-color-default-hover)', borderLeft: '1px solid var(--mantine-color-default-border)' }}>
                  <Box>
                    <Group justify="space-between" align="center" px={{ base: 'lg', md: 'xl' }} pt="lg" pb={4} wrap="nowrap">
                      <Text {...EYEBROW}>Report</Text>
                      <CopyButton value={slipText(kind, title, details)} timeout={2000}>
                        {({ copied, copy }) => (
                          <Button size="compact-sm" variant="subtle" color="gray" c="dimmed" onClick={copy} leftSection={copied ? <IconCheck {...ICON_INLINE} /> : <IconCopy {...ICON_INLINE} />}>
                            {copied ? 'Copied' : 'Copy'}
                          </Button>
                        )}
                      </CopyButton>
                    </Group>
                    <Text size="sm" c="dimmed" px={{ base: 'lg', md: 'xl' }} pb="md">Include this when you write to MotionSafe.</Text>
                    <Box component="dl" m={0} px={{ base: 'lg', md: 'xl' }} pb="lg" style={{ display: 'grid', gridTemplateColumns: 'max-content minmax(0, 1fr)', gap: '10px var(--mantine-spacing-lg)', alignItems: 'baseline' }}>
                      {details.map((d, i) => (
                        <Box key={`${d.label}-${i}`} style={{ display: 'contents' }}>
                          <Text component="dt" size="sm" c="dimmed">{d.label}</Text>
                          <Text component="dd" size="sm" m={0} className="id" style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{d.value}</Text>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Stack>
              )}
            </SimpleGrid>
          </Paper>
        </Stack>
      </Box>
      <Box component="footer" p="md" ta="center">
        <Text size="xs" c="dimmed">Operators sign in on a device, not here.</Text>
      </Box>
    </Box>
  );
}

/** The moment, for a person relaying what they saw. */
export function nowLabel(): string {
  return new Date().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}
